/**
 * Removes duplicate entries in an array. Maintains ordering.
 * @param records - Array to make unique.
 * @param valueFn - Function which determines what value to check uniqueness on.
 */
export function arrayUnique<T, K>(records: T[], valueFn?: (record: T) => K): T[] {
  const fn = typeof valueFn === 'undefined' ? (r: T): T => r : valueFn;
  return records.filter((elem, pos, arr) => {
    return arr.map((a) => fn(a)).indexOf(fn(elem)) === pos;
  });
}

/**
 * Removes duplicate entries in an array. Maintains ordering. Alias for arrayUnique(...).
 * @param records - Array to make unique.
 * @param valueFn - Function which determines what value to check uniqueness on.
 */
export function arrayDistinct<T, K>(records: T[], valueFn?: (record: T) => K): T[] {
  return arrayUnique<T, K>(records);
}

/**
 * Checks if every item in two arrays are the same, and if array length is same.
 * @param arr1 - First array
 * @param arr2 - Second array
 */
export function isEqualArrays(arr1: string[], arr2: string[]): boolean {
  return (
    Array.isArray(arr1) &&
    Array.isArray(arr2) &&
    arr1.length === arr2.length &&
    arr1.every((item, index) => item === arr2[index])
  );
}

/**
 * Joins two sets of records based on a particular field.
 *
 * const leftRecords = [{"name": "Bob", "age": 53}, {"name": "Roy", "age": 31}]
 * const rightRecords = [{"firstName": "Bob", "size": 32}]
 * const joinedRecords = arrayJoin(leftRecords, rightRecords, r => name, r => firstName, 'left')
 * joinedRecords -> [
 *    {"left": {"name": "Bob", "age": 53}, "right": {"firstName": "Bob", "size": 32}},
 *    {"left": {"name": "Roy", "age": 31}, "right": null},
 * ]
 *
 * const newRecords = joinedRecords.map(r => {"name": r.left.name, "size": r.right.size})
 * newRecords -> [
 *    {"name": "Bob", "size": 32}},
 *    {"name": "Roy", "size": null},
 * ]
 *
 * @param leftArray - Left array of records.
 * @param rightArray - Right array of records.
 * @param leftKey - Function which determines what field to use for the left array.
 * @param rightKey - Function which determines what field to use for the right array.
 * @param joinType - When set to "left", will keep rows if no match with right records. Will remove records if no match with right records.
 */
export function arrayJoin<L, R>(
  leftArray: L[],
  rightArray: R[],
  leftKey: (left: L) => unknown,
  rightKey: (right: R) => unknown,
  joinType?: string,
): { left: L; right: R | null }[] {
  // TODO: Optimize

  const outputs: { left: L; right: R | null }[] = [];

  leftArray.forEach((left) => {
    let count = 0;
    rightArray.forEach((right) => {
      if (leftKey(left) === rightKey(right)) {
        const output = {
          left: left,
          right: right,
        };
        outputs.push(output);
        count++;
      }
    });
    if (joinType && joinType === 'left' && count === 0) {
      const output = {
        left: left,
        right: null,
      };
      outputs.push(output);
    }
  });

  return outputs;
}

/**
 * Returns the record with the extreme value of a callback (typically a property).
 * It is based on a comparator function.
 *
 * const records = [{"name": "Bob", "age": 53}, {"name": "Roy", "age": 31}]
 * const extremeRecord = arrayExtreme(records, record => record.age, (age1, age2) => age1 - age2)
 * extremeRecord -> {"name": "Bob", "age": 53}
 *
 * @param records - The array of records to search among.
 * @param valueFn - A function which determines the value to compare records with.
 * @param operator - A comparator function which determines which value is more extreme.
 */
export function arrayExtreme<T, K>(
  records: T[],
  valueFn: (record: T) => K,
  operator: (left: K, right: K) => number,
): T | null {
  // I'm amazed that this function was necessary to make.
  // Motivation: https://stackoverflow.com/questions/4020796/finding-the-max-value-of-an-attribute-in-an-array-of-objects
  // This implementation is O(n). Other one-liner versions I found were ugly or O(n*log(n)) (i.e., sorting).
  if (!records || records.length === 0) {
    return null;
  }
  let currentValue = valueFn(records[0]);
  let recordOutput = records[0];
  for (let idx = 1; idx < records.length; idx++) {
    const record = records[idx];
    const value = valueFn(record);
    if (operator(value, currentValue) < 0) {
      currentValue = value;
      recordOutput = record;
    }
  }
  return recordOutput;
}

/**
 * Returns the record with the minimum value of a callback (typically a property).
 *
 * const records = [{"name": "Bob", "age": 53}, {"name": "Roy", "age": 31}]
 * const minRecord = arrayMin(records, r => r.age)
 * minRecord -> {"name": "Roy", "age": 31}
 *
 * @param records - The array of records to search among.
 * @param valueFn - A function which determines the value to compare records with.
 */
export function arrayMin<T, K>(records: T[], valueFn: (record: T) => K): T | null {
  return arrayExtreme(records, valueFn, (left, right) => (left < right ? -1 : 1));
}

/**
 * Returns the record with the maximum value of a callback (typically a property).
 *
 * const records = [{"name": "Bob", "age": 53}, {"name": "Roy", "age": 31}]
 * const maxRecord = arrayMax(records, r => r.age)
 * maxRecord -> {"name": "Bob", "age": 53}
 *
 * @param records - The array of records to search among.
 * @param valueFn - A function which determines the value to compare records with.
 */
export function arrayMax<T, K>(records: T[], valueFn: (record: T) => K): T | null {
  return arrayExtreme(records, valueFn, (left, right) => (left > right ? -1 : 1));
}

/**
 * Returns true if all records are true, where what is true is determined by a callback (typically a property).
 *
 * const records = [{"name": "Bob", "age": 53}, {"name": "Roy", "age": 31}]
 * const everyoneIsOld = arrayAll(records, r => r.age > 30)
 * everyoneIsOld -> true
 *
 * @param records - The array of records to operate on.
 * @param valueFn - A function which determines the value to do boolean logic on.
 */
export function arrayAll<T>(records: T[], valueFn?: (record: T) => boolean): boolean {
  const fn = typeof valueFn === 'undefined' ? (r: T): boolean => !!r : valueFn;
  return records.map((r) => fn(r)).reduce((sum, current) => sum && current, true);
}

/**
 * Returns true if at least one records is true, where what is true is determined by a callback (typically a property).
 *
 * const records = [{"name": "Bob", "age": 53}, {"name": "Roy", "age": 31}]
 * const someoneIsVeryOld = arrayAny(records, r => r.age > 50)
 * someoneIsVeryOld -> true
 *
 * @param records - The array of records to operate on.
 * @param valueFn - A function which determines the value to do boolean logic on.
 */
export function arrayAny<T>(records: T[], valueFn?: (record: T) => boolean): boolean {
  const fn = typeof valueFn === 'undefined' ? (r: T): boolean => !!r : valueFn;
  return records.map((r) => fn(r)).reduce((sum, current) => sum || current, false);
}

/**
 * Creates a DictList based on an array of records (i.e., any object). Uses the records themselves as the DictList values.
 * @param records - The array of records to convert to a DictList.
 * @param keyFn - A function which determines how to construct the DictList field based on a record. Typically just returns a property of the record.
 * @param compareFn - A comparator function which determines the ordering of values for the same field.
 */
export function arrayToDictList<T>(
  records: T[],
  keyFn: (record: T) => string | number,
  compareFn?: (a: T, b: T) => number,
): DictList<T> {
  return arrayToPickyDictList(records, keyFn, (v) => v, compareFn);
}

/**
 * Creates a DictList based on an array of records (i.e., any object).
 * @param records - The array of records to convert to a DictList.
 * @param keyFn - A function which determines how to construct the DictList field based on a record. Typically just returns a property of the record.
 * @param valueFn - A function which determines how to construct the DictList values based on a record. Typically just returns the records themselves.
 * @param compareFn - A comparator function which determines the ordering of values for the same field.
 */
export function arrayToPickyDictList<T, V>(
  records: T[],
  keyFn: (record: T) => string | number,
  valueFn: (record: T) => V,
  compareFn?: (a: V, b: V) => number,
): DictList<V> {
  const simpleDictList: DictList<V> = {};
  for (const record of records) {
    const value = valueFn(record);
    const key = keyFn(record);
    let dictListEntry = simpleDictList[key];
    if (dictListEntry) {
      // Dict list entry already exists
    } else {
      dictListEntry = [];
      simpleDictList[key] = dictListEntry;
    }
    dictListEntry.push(value);
    if (compareFn) {
      // TODO: Optimize by moving out of loop.
      dictListEntry.sort(compareFn);
    }
  }
  return simpleDictList;
}

export function includes<T>(haystack: { indexOf: (entry: T) => number }, needle: T): boolean {
  if (haystack == null) {
    return false;
  }
  return haystack.indexOf(needle) > -1;
}

export function includesAll<T>(haystack: { indexOf: (entry: T) => number }, needles: T[]): boolean {
  return arrayAll(needles.map((n) => includes(haystack, n)));
}

export function includesAny<T>(haystack: { indexOf: (entry: T) => number }, needles: T[]): boolean {
  return needles.filter((needle) => includes(haystack, needle)).length > 0;
}

/**
 * Creates a Dict based on an array of records (i.e., any object). Uses the record itself as the Dict value.
 * @param records - The array of records to convert to a Dict.
 * @param keyFn - A function which determines how to construct the Dict field based on a record. Typically just returns a property of the record.
 * @param compareFn - A comparator function which determines which record to choose if there are multiple records which match the same field.
 */
export function arrayToDict<R>(
  records: R[],
  keyFn: (record: R) => string,
  compareFn?: (a: R, b: R) => number,
): Dict<R> {
  return arrayToPickyDict(records, keyFn, (record) => record, compareFn);
}

/**
 * Creates a Dict based on an array of records (i.e., any object).
 * @param records - The array of records to convert to a Dict.
 * @param keyFn - A function which determines how to construct the Dict field based on a record. Typically just returns a property of the record.
 * @param valueFn - A function which determines how to construct the Dict value based on a record. Typically just returns the record itself.
 * @param compareFn - A comparator function which determines which value to choose if there are multiple records which match the same field.
 */
export function arrayToPickyDict<R, V>(
  records: R[],
  keyFn: (record: R) => string,
  valueFn: (record: R) => V,
  compareFn?: (a: R, b: R) => number,
): Dict<V> {
  const recordDict: Dict<R> = {};
  const valueDict: Dict<V> = {};
  for (const newRecord of records) {
    const key = keyFn(newRecord);
    const existingRecord = recordDict[key];
    if (!existingRecord || (compareFn && compareFn(existingRecord, newRecord) > 0)) {
      recordDict[key] = newRecord;
      valueDict[key] = valueFn(newRecord);
    }
  }
  return valueDict;
}

/**
 * Returns all keys in Dict or DictList.
 */
export function dictToKeys<T>(simpleDict: DictList<T> | Dict<T>): string[] {
  const keys: string[] = [];
  for (const key in simpleDict) {
    if (simpleDict.hasOwnProperty(key)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Counts number of keys in a Dict or DictList.
 */
export function dictKeyCount<T>(simpleDict: DictList<T> | Dict<T>): number {
  return dictToKeys(simpleDict).length;
}

/**
 * Returns all values of a dict.
 */
export function dictToValues<V>(simpleDict: Dict<V>): V[] {
  const values: V[] = [];
  for (const key in simpleDict) {
    if (simpleDict.hasOwnProperty(key)) {
      values.push(simpleDict[key]);
    }
  }
  return values;
}

/**
 * Returns all values of a dict/object.
 */
export function dictToKeyValueItems<T>(obj: Dict<T>): { key: string; value: T }[] {
  const keyValues: { key: string; value: T }[] = [];
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      keyValues.push({ key: key, value: obj[key] });
    }
  }
  return keyValues;
}

/**
 * Returns a filtered DictList. It removes elements based on the dict field.
 * const testDictList = {"foo": [42, 10], "bar": [21, 12]} as DictList;
 * const filteredTestDictList = filterDict(testDictList, field => field === 'foo')
 * filteredTestDictList -> {"foo": [42, 10]}
 */
export function filterDictList<T>(dictList: DictList<T>, filterFn: (key: string) => boolean): DictList<T> {
  const copy = new DictList<T>();
  const keys = dictToKeys(dictList).filter(filterFn);
  for (const key of keys) {
    delete copy[key];
  }
  return copy;
}

/**
 * Removes elements from Dict based initialFilter function with field as input.
 * const testDict = {"foo": 42, "bar": 21} as Dict;
 * const filteredTestDict = filterDict(testDict, field => field === 'foo')
 * filteredTestDict -> {"foo": 42}
 */
export function filterDict<T>(dict: Dict<T>, filterFn: (key: string) => boolean): Dict<T> {
  const copy = new Dict<T>();
  const keys = dictToKeys(dict).filter(filterFn);
  for (const key of keys) {
    delete copy[key as string];
  }
  return copy;
}

/**
 * Filter with a limit. Unlike regular array filter this one will stop when the number of occurrences will reach the limit.
 * @param items - The array of items to filter
 * @param compareFn - A comparator function
 * @param limit - the number of occurrences to take
 */
export function findMany<T>(items: T[], compareFn: (i: T) => boolean, limit: number): T[] {
  if (!(compareFn || compareFn)) {
    return [];
  }
  const result: T[] = [];
  for (const item of items) {
    if (compareFn(item)) {
      result.push(item);
    }
    if (result.length === limit) {
      return result;
    }
  }
  return result;
}

export class DictList<V> {
  [key: string]: V[];
}

export class Dict<V> {
  [key: string]: V;
}

// https://stackoverflow.com/a/53593328/604048
export function jsonOrderedStringify(obj: object, space?: any): string {
  const allKeys: any[] = [];
  const seen: any = {};
  JSON.stringify(obj, function (key, value) {
    if (!(key in seen)) {
      allKeys.push(key);
      seen[key] = null;
    }
    return value;
  });
  allKeys.sort();
  return JSON.stringify(obj, allKeys, space);
}

/**
 * Updates an item in an array at a specified index with a new value and returns a new updated array.
 */
export function updateItemInArray<T>(array: T[], updatedItem: T, index: number): T[] {
  return array.map((item, idx) => (idx === index ? updatedItem : item));
}

/**
 * Inserts or updates an item in an array.
 *
 * If the item exists (based on the search function), it will be updated.
 * If the item does not exist, it will be appended to the array.
 */
export function upsertItemInArray<T>(array: T[], updatedItem: T, searchFn: (array: T[]) => number): T[] {
  const index = searchFn(array);

  if (index === -1) {
    // Item not found, append it to the array
    return [...array, updatedItem];
  }

  // Item found, create a shallow copy with the updated item
  const newArray = [...array];
  newArray[index] = updatedItem;
  return newArray;
}
